import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import api from "../services/api";
import { getMe, resetMeCache } from "../services/me";

function normalizeDigits(value) {
  return (value || "").toString().replace(/\D/g, "");
}

export default function TenantSetupPage() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, userId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [facilityName, setFacilityName] = useState("");
  const [createSaving, setCreateSaving] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteSaving, setInviteSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isLoaded) return;
      if (!isSignedIn) {
        setLoading(false);
        setMe(null);
        return;
      }

      try {
        const nextMe = await getMe({ cacheKey: userId, forceRefresh: true });
        if (cancelled) return;
        setMe(nextMe || null);
      } catch {
        if (cancelled) return;
        setMe(null);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, userId]);

  async function handleCreateFacility() {
    setError("");
    setCreateSaving(true);
    try {
      await api.post("/tenant/bootstrap", { name: facilityName.trim() || "My Facility" });
      resetMeCache();
      await getMe({ cacheKey: userId, forceRefresh: true });
      navigate("/admin/billing", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to create facility");
    } finally {
      setCreateSaving(false);
    }
  }

  async function handleRedeemInvite() {
    setError("");
    setInviteSaving(true);
    try {
      await api.post("/tenant/otp/redeem-join", { code: inviteCode });
      resetMeCache();
      const nextMe = await getMe({ cacheKey: userId, forceRefresh: true });

      const role = nextMe?.role;
      const nextPath = role === "admin" ? "/admin" : "/staff";
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to redeem invite code");
    } finally {
      setInviteSaving(false);
    }
  }

  return (
    <>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>

      <SignedIn>
        <div style={{ padding: 24, maxWidth: 820, margin: "0 auto" }}>
          <h2>Set up your facility</h2>
          <p style={{ marginTop: 4, color: "#555" }}>
            If you are the new facility owner, create your facility first. If you were invited to an existing facility, enter the 6-digit invite code sent to your email.
          </p>

          {loading ? (
            <p>Loading…</p>
          ) : me?.tenantId ? (
            <Navigate to={me?.role === "admin" ? "/admin" : "/staff"} replace />
          ) : (
            <>
              {error ? (
                <div style={{ background: "#fff3f3", border: "1px solid #ffd7d7", padding: 12, borderRadius: 8, marginTop: 16 }}>
                  <strong style={{ color: "#b00020" }}>Error:</strong> {error}
                </div>
              ) : null}

              <div style={{ marginTop: 16, maxWidth: 480 }}>

                {/* ── Create new facility ── */}
                <div style={{ border: "2px solid #111", borderRadius: 10, padding: 20, background: "white" }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>🏠 Create a new facility</div>
                  <div style={{ color: "#555", marginBottom: 12, fontSize: 14 }}>
                    You are the facility owner. Enter your facility name and we will set up your account.
                  </div>

                  <label style={{ display: "block", fontSize: 13, color: "#444", fontWeight: 600 }}>Facility name</label>
                  <input
                    value={facilityName}
                    onChange={(e) => setFacilityName(e.target.value)}
                    placeholder="e.g. Sunrise Home Care"
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 8,
                      border: "1px solid #ccc",
                      marginTop: 6,
                      boxSizing: "border-box",
                      fontSize: 15,
                    }}
                  />

                  <button
                    onClick={handleCreateFacility}
                    disabled={createSaving}
                    style={{
                      marginTop: 14,
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: "none",
                      background: "#111",
                      color: "#fff",
                      cursor: createSaving ? "not-allowed" : "pointer",
                      width: "100%",
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    {createSaving ? "Creating…" : "Create facility & continue →"}
                  </button>
                </div>

                {/* ── OR divider ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
                  <div style={{ flex: 1, height: 1, background: "#e0e0e0" }} />
                  <span style={{ color: "#999", fontSize: 13, fontWeight: 600 }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: "#e0e0e0" }} />
                </div>

                {/* ── Join with invite code ── */}
                <div style={{ border: "1px solid #e5e5e5", borderRadius: 10, padding: 20, background: "white" }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Join an existing facility</div>
                  <div style={{ color: "#555", marginBottom: 12, fontSize: 14 }}>
                    Use this only if an admin already created the facility and sent you a 6-digit invite code.
                  </div>

                  <label style={{ display: "block", fontSize: 13, color: "#444" }}>Invite code</label>
                  <input
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="123456"
                    inputMode="numeric"
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      marginTop: 6,
                      fontFamily: "monospace",
                      letterSpacing: "0.18em",
                    }}
                  />

                  <div style={{ marginTop: 8, color: "#777", fontSize: 13 }}>
                    You entered: <span style={{ fontFamily: "monospace" }}>{normalizeDigits(inviteCode) || "—"}</span>
                  </div>

                  <button
                    onClick={handleRedeemInvite}
                    disabled={inviteSaving || normalizeDigits(inviteCode).length !== 6}
                    style={{
                      marginTop: 14,
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid #111",
                      background: "#111",
                      color: "#fff",
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    {inviteSaving ? "Verifying…" : "Join with invite code"}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/post-sign-in", { replace: true })}
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #111",
                  background: "#fff",
                  color: "#111",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                Back
              </button>
            </>
          )}
        </div>
      </SignedIn>
    </>
  );
}
